import React from "react";
import { Info, Users } from "lucide-react";

import { Card } from "@/components/ui/Card";
import DataTable from "@/components/shared/DataTable";
import UsageSearchField from "./UsageSearchField";
import { usageRowKey } from "./usageRowKey";
import UsageSectionHeader from "./UsageSectionHeader";
import { fmt, fmtPct, pctOf } from "./format";

/**
 * Per-user usage breakdown.
 *
 * Two things this component exists to get right:
 *
 * 1. **The System / candidate-initiated row.** Public candidate apply is
 *    unauthenticated, and the candidate matching that runs automatically after
 *    one is not even passed a user id, so that work has no owner. It is
 *    bucketed here — never spread across named users, never hidden. A
 *    re-analysis someone asked for is not in this bucket: it lands on their own
 *    row, because the re-trigger batch knows who requested it.
 *
 * 2. **Saying out loud that the numbers do not add up.** Because of (1), the
 *    tenant total is legitimately higher than the sum of the named rows. Left
 *    unexplained, that reads as a bug, so `unattributedNote` from the API is
 *    rendered prominently rather than tucked away.
 *
 * Search is server-side on **both** screens that render this table, which is
 * why the input is debounced by the caller and no row is filtered here. The
 * server matches on the label the reader can see — the unattributed rows
 * included, so the words printed on them can be looked up — sums the totals
 * over what is left, and sends the note that matches. Filtering locally would
 * show those totals against a different set of rows.
 *
 * The totals row names its own scope while a search is active, because the
 * summary cards above it are a separate call that knows nothing about the
 * search. The two legitimately disagree, and the reader is told which is
 * which rather than left to spot it.
 *
 * The share bar behind each name is the one piece of chrome this table has. It
 * turns a column of similar-looking numbers into a shape, so the heavy user is
 * visible before anything is read.
 *
 * The LLM-call columns are engineering detail. They appear only when the
 * fields are present, which is only when a SuperAdmin asked for them — the
 * tenant response models have no field to put them in. Presence, never role:
 * no component in this directory tests the viewer's role.
 */
const UsageByUserTable = ({ data, search, onSearchChange, loading }) => {
  const rows = data?.rows ?? [];
  const showCalls = rows.some((row) => row.llmCalls !== undefined && row.llmCalls !== null);
  const peak = rows.reduce((max, row) => Math.max(max, Number(row.totalTokens ?? 0)), 0);

  // Sorting is off for the same reason filtering is: the server ordered these
  // rows and sent totals that match that set. `pageSize` is the row count, so
  // the shared table shows every row and draws no pager.
  const columns = [
    {
      key: "name",
      label: "User",
      render: (_value, row) => (
        <>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-text-primary">{row.name}</span>
            {row.email && <span className="text-xs text-text-secondary">{row.email}</span>}
            {row.isUnattributed && <span className="rounded-full bg-tone-slate-soft px-2 py-0.5 text-[10px] text-tone-slate">no signed-in user</span>}
          </div>
          {/* Proportional to the heaviest row, not to the tenant total: the
              question here is who stands out among these people. */}
          <div className="mt-1.5 h-1 w-full max-w-[220px] overflow-hidden rounded-full bg-background-tertiary">
            <div className="h-full rounded-full bg-tone-blue" style={{ width: `${Math.max(2, pctOf(row.totalTokens, peak))}%` }} />
          </div>
        </>
      ),
    },
    { key: "uses", label: "Tool uses", align: "right", className: "tabular-nums", render: (value) => fmt(value) },
    ...(showCalls
      ? [
          { key: "llmCalls", label: "LLM calls", align: "right", className: "tabular-nums", render: (value) => fmt(value) },
          { key: "cachedCalls", label: "Cached", align: "right", className: "tabular-nums", render: (value) => fmt(value) },
        ]
      : []),
    {
      key: "totalTokens",
      label: "Tokens",
      align: "right",
      className: "tabular-nums",
      render: (value) => (
        <>
          {fmt(value)}
          <span className="ml-2 text-[11px] text-text-secondary">{fmtPct(pctOf(value, data?.totalTokens))}</span>
        </>
      ),
    },
  ];

  return (
    <Card className="p-5">
      <UsageSectionHeader
        icon={Users}
        title="By user"
        subtitle="Who used which tools in this period."
        aside={
          // `loading` puts the spinner where the clear button is, so a
          // server-side search never shifts the row.
          <UsageSearchField value={search} onChange={onSearchChange} placeholder="Search name or email" loading={loading} inputClassName="min-w-[240px]" />
        }
      />

      {data?.unattributedNote && (
        <p className="mb-4 flex items-start gap-2 rounded-lg border border-border-primary bg-background-primary p-3 text-[12px] leading-relaxed text-text-secondary">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-secondary" />
          <span>{data.unattributedNote}</span>
        </p>
      )}

      {/* The table is dropped when there is nothing in it, rather than left to
          print its own generic "No data available" — which does not say whether
          the period was quiet or the search was too narrow. */}
      {rows.length > 0 ? (
        <DataTable columns={columns} rowKey={usageRowKey} apiData={rows} sortable={false} showPagination={false} pageSize={rows.length} />
      ) : (
        <p className="py-10 text-center text-sm text-text-secondary">
          {loading ? "Loading…" : search ? `No user matches "${search}" in this period.` : "No usage recorded in this period."}
        </p>
      )}

      {/* The totals sit outside the table rather than in a `tfoot`: the shared
          table renders rows only, and a totals row pushed through it as data
          would sort and paginate with the people. */}
      {rows.length > 0 && (
        <div className="mt-3 flex flex-wrap items-baseline justify-end gap-x-6 gap-y-1 border-t border-border-primary pt-3 text-sm font-medium text-text-primary">
          <span className="mr-auto">{search ? "Total for these rows" : "Total"}</span>
          <span className="tabular-nums">
            <span className="mr-1.5 text-[11px] font-normal uppercase tracking-wide text-text-secondary">Tool uses</span>
            {fmt(data?.totalUses)}
          </span>
          {showCalls && (
            <span className="tabular-nums">
              <span className="mr-1.5 text-[11px] font-normal uppercase tracking-wide text-text-secondary">LLM calls</span>
              {fmt(data?.totalLlmCalls)}
            </span>
          )}
          <span className="tabular-nums">
            <span className="mr-1.5 text-[11px] font-normal uppercase tracking-wide text-text-secondary">Tokens</span>
            {fmt(data?.totalTokens)}
          </span>
        </div>
      )}
    </Card>
  );
};

export default UsageByUserTable;
